import classNames from "classnames"
import { Formik, useFormikContext } from "formik"

// Tag filter (dropdown + mobile sheet) temporarily disabled.
// import ArticleSortDropdown from "./ArticleSortDropdown"
// import ArticleSortSheet from "./ArticleSortSheet"
import CategoryTab from "./CategoryTab"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import Callout from "@components/elements/V2/Callout"
import PlusIcon from "@components/elements/V2/Icons/PlusIcon"
import SearchIcon from "@components/elements/V2/Icons/SearchIcon"
import TextInput from "@components/elements/V2/Inputs/TextInput"
import Typography from "@components/elements/V2/Typography"
import ArticlePreviewCard from "@components/V2/Cards/Content/ArticlePreview"
import GridSection from "@components/V2/Layouts/GridSection"
import { Color } from "@constants/V2/color"
import { useKnowledgeHubArticles } from "@hooks/V2/storyblok/useKnowledgeHubArticles"
import { useArticleOverviewRoute } from "@hooks/V2/useArticleOverviewRoute"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import {
  getAccentColor,
  getFillColorClass,
  getTransparentBorderColorClass,
} from "@utils/V2/color"
import React, { useEffect, useMemo, useState } from "react"

const DEFAULT_INITIAL_ARTICLE_COUNT = 6
const LATEST_OPTION_VALUE = ""
// Tag filter temporarily disabled — only used by getTagOrder/getTagsFromArticles below.
// const ADDITIONAL_TAG_ORDER = ["hardware", "payments", "business tools"]

type SearchValues = {
  search: string
}

const INITIAL_SEARCH_VALUES: SearchValues = { search: "" }

const normaliseValue = (value: string | undefined) =>
  value ? value.trim().toLowerCase() : ""

/**
 * Storyblok number fields arrive as strings, and an empty field arrives as an
 * empty string, so anything that is not a positive number falls back.
 */
const parseInitialArticleCount = (initialNumArticles?: string) => {
  const parsedCount = Math.floor(Number(initialNumArticles))

  return Number.isFinite(parsedCount) && parsedCount >= 1
    ? parsedCount
    : DEFAULT_INITIAL_ARTICLE_COUNT
}

const getCategoriesFromArticles = (
  articles: Storyblok.ArticleListItem[]
): Storyblok.ArticleCategoryTab[] => {
  const seenCategories = new Map<string, string>()

  for (const article of articles) {
    const category = article.content.category?.trim()

    if (category && !seenCategories.has(normaliseValue(category))) {
      seenCategories.set(normaliseValue(category), category)
    }
  }

  return [...seenCategories.entries()].map(([slug, name]) => ({ slug, name }))
}

/** Order matters: the design leads with featured and follows with video. */
const FULL_WIDTH_PREVIEW_STYLES: Storyblok.ArticlePreviewStyle[] = [
  "featured",
  "video",
]
const DEFAULT_ARTICLES_BETWEEN_FULL_WIDTH_CARDS = 6

const getFullWidthStyleRank = (article: Storyblok.ArticleListItem) => {
  const { previewStyle } = article.content

  return previewStyle ? FULL_WIDTH_PREVIEW_STYLES.indexOf(previewStyle) : -1
}

const isFullWidthArticle = (article: Storyblok.ArticleListItem) =>
  getFullWidthStyleRank(article) !== -1

const interleaveFullWidthArticles = (
  visibleDefaultArticles: Storyblok.ArticleListItem[],
  fullWidthArticles: Storyblok.ArticleListItem[],
  hasMoreDefaultArticles: boolean
) => {
  const orderedArticles: Storyblok.ArticleListItem[] = []
  let placedFullWidthCount = 0

  for (const [index, article] of visibleDefaultArticles.entries()) {
    orderedArticles.push(article)

    const isIntervalBoundary =
      (index + 1) % DEFAULT_ARTICLES_BETWEEN_FULL_WIDTH_CARDS === 0

    if (isIntervalBoundary && placedFullWidthCount < fullWidthArticles.length) {
      orderedArticles.push(fullWidthArticles[placedFullWidthCount])
      placedFullWidthCount += 1
    }
  }

  if (hasMoreDefaultArticles) {
    return orderedArticles
  }

  return [...orderedArticles, ...fullWidthArticles.slice(placedFullWidthCount)]
}

const getArticlesForCategory = (
  articles: Storyblok.ArticleListItem[],
  categorySlug: string | null
) =>
  categorySlug === null
    ? articles
    : articles.filter(
        (article) =>
          normaliseValue(article.content.category) ===
          normaliseValue(categorySlug)
      )

// Tag filter temporarily disabled — only used by sortOptions below.
// const getTagOrder = (tagValue: string) => {
//   const tagIndex = ADDITIONAL_TAG_ORDER.indexOf(tagValue)
//
//   return tagIndex === -1 ? ADDITIONAL_TAG_ORDER.length : tagIndex
// }
//
// const getTagsFromArticles = (articles: Storyblok.ArticleListItem[]) => {
//   const seenTags = new Map<string, string>()
//
//   for (const article of articles) {
//     const additionalTag = article.content.additionalTag?.trim()
//
//     if (additionalTag && !seenTags.has(normaliseValue(additionalTag))) {
//       seenTags.set(normaliseValue(additionalTag), additionalTag)
//     }
//   }
//
//   return [...seenTags.entries()]
//     .map(([value, label]) => ({ value, label }))
//     .sort(
//       (tag, comparedTag) =>
//         getTagOrder(tag.value) - getTagOrder(comparedTag.value)
//     )
// }

type ArticleFilterSectionProps = {
  callout?: Storyblok.Callout
  superscriptText?: string
  headingText?: string
  highlightedText?: string
  headingBodyText?: string
  searchPlaceholder?: string
  allTabLabel?: string
  sortLabel?: string
  latestOptionLabel?: string
  applyFilterText?: string
  loadMoreText?: string
  initialNumArticles?: string
  emptyStateText?: string
  textColor?: Color
}

type ArticleListProps = {
  searchPlaceholder: string
  allTabLabel: string
  sortLabel: string
  latestOptionLabel: string
  applyFilterText: string
  loadMoreText: string
  emptyStateText: string
  textColor: Color
  initialArticleCount: number
}

const ArticleList = ({
  searchPlaceholder,
  allTabLabel,
  loadMoreText,
  emptyStateText,
  textColor,
  initialArticleCount,
}: ArticleListProps) => {
  const articles = useKnowledgeHubArticles()
  const articleOverviewRoute = useArticleOverviewRoute()
  const { values } = useFormikContext<SearchValues>()

  // Tag filter temporarily disabled — selectedTag stays at its initial value.
  const [selectedTag] = useState(LATEST_OPTION_VALUE)
  const [visibleCount, setVisibleCount] = useState(initialArticleCount)

  const searchTerm = values.search
  const activeCategory = articleOverviewRoute?.activeCategory ?? null
  const overviewBasePath = articleOverviewRoute?.overviewBasePath
  const categoryTabs =
    articleOverviewRoute?.categoryTabs ?? getCategoriesFromArticles(articles)

  useEffect(() => {
    setVisibleCount(initialArticleCount)
  }, [searchTerm, selectedTag, initialArticleCount])

  const categoryArticles = useMemo(
    () => getArticlesForCategory(articles, activeCategory),
    [articles, activeCategory]
  )

  // Tag filter temporarily disabled — sortOptions no longer consumed.
  // const sortOptions = useMemo(
  //   () => [
  //     { label: latestOptionLabel, value: LATEST_OPTION_VALUE },
  //     ...getTagsFromArticles(categoryArticles),
  //   ],
  //   [categoryArticles, latestOptionLabel]
  // )

  const filteredArticles = useMemo(() => {
    const normalisedSearchTerm = normaliseValue(searchTerm)

    return categoryArticles.filter((article) => {
      const matchesTag =
        selectedTag === LATEST_OPTION_VALUE ||
        normaliseValue(article.content.additionalTag) === selectedTag

      const matchesSearch =
        !normalisedSearchTerm ||
        normaliseValue(article.content.title).includes(normalisedSearchTerm)

      return matchesTag && matchesSearch
    })
  }, [categoryArticles, selectedTag, searchTerm])

  const defaultArticles = useMemo(
    () => filteredArticles.filter((article) => !isFullWidthArticle(article)),
    [filteredArticles]
  )

  const fullWidthArticles = useMemo(
    () =>
      filteredArticles
        .filter(isFullWidthArticle)
        .sort(
          (article, comparedArticle) =>
            getFullWidthStyleRank(article) -
            getFullWidthStyleRank(comparedArticle)
        ),
    [filteredArticles]
  )

  const visibleDefaultArticles = defaultArticles.slice(0, visibleCount)
  const hasMoreArticles = defaultArticles.length > visibleDefaultArticles.length
  const visibleArticles = interleaveFullWidthArticles(
    visibleDefaultArticles,
    fullWidthArticles,
    hasMoreArticles
  )

  return (
    <>
      <div className="col-span-full flex flex-wrap items-center gap-20 md:justify-center md:gap-32">
        <div className="flex basis-full items-center gap-10 md:basis-auto">
          <TextInput
            id="search"
            name="search"
            type="text"
            placeholder={searchPlaceholder}
            icon={
              <span className="flex h-[36px] w-[38px] items-center justify-center rounded-40 md:bg-charcoal/[8%]">
                <SearchIcon className="h-24 w-24" />
              </span>
            }
            iconPosition="right"
            className="min-w-0 flex-1 rounded-32 ring-[3px] ring-charcoal/5 md:w-[190px] md:flex-none [&_input]:py-[13px] [&_input]:pl-20 [&_input]:placeholder:text-charcoal/60"
            isRounded
          />

          {/* Tag filter temporarily disabled — see index.tsx ArticleSortDropdown comment below. */}
          {/* <ArticleSortSheet
            options={sortOptions}
            selectedValue={selectedTag}
            onSelect={setSelectedTag}
            sortLabel={sortLabel}
            applyFilterText={applyFilterText}
            textColor={textColor}
          /> */}
        </div>

        <div className="min-w-0 basis-full md:basis-auto">
          <div
            className={classNames(
              "-mx-gutter-xs flex items-center gap-8 overflow-x-auto border-b px-gutter-xs pb-20 [scrollbar-width:none] md:mx-0 md:flex-wrap md:gap-10 md:overflow-visible md:border-b-0 md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden",
              getTransparentBorderColorClass(textColor)
            )}
          >
            <CategoryTab
              label={allTabLabel}
              count={articles.length}
              linkPath={overviewBasePath}
              isActive={activeCategory === null}
              textColor={textColor}
            />
            {categoryTabs
              .filter(
                (categoryTab) =>
                  getArticlesForCategory(articles, categoryTab.slug).length > 0
              )
              .map((categoryTab) => (
                <CategoryTab
                  key={categoryTab.slug}
                  label={categoryTab.name}
                  count={
                    getArticlesForCategory(articles, categoryTab.slug).length
                  }
                  linkPath={
                    overviewBasePath
                      ? `${overviewBasePath}${categoryTab.slug}/`
                      : undefined
                  }
                  isActive={activeCategory === categoryTab.slug}
                  textColor={textColor}
                />
              ))}
          </div>
        </div>

        {/* Tag filter (dropdown + mobile sheet) temporarily disabled. */}
        {/* <ArticleSortDropdown
          options={sortOptions}
          selectedValue={selectedTag}
          onSelect={setSelectedTag}
          sortLabel={sortLabel}
          textColor={textColor}
        /> */}
      </div>

      {visibleArticles.length === 0 ? (
        <Typography
          className="col-span-full"
          size="lead-md"
          font="grotesk"
          weight="book"
          color={textColor}
          text={emptyStateText}
        />
      ) : (
        visibleArticles.map((article) => (
          <ArticlePreviewCard
            key={article.uuid}
            article={article}
            textColor={textColor}
            previewStyle={article.content.previewStyle ?? "default"}
          />
        ))
      )}

      {hasMoreArticles && (
        <div className="col-span-full flex justify-center">
          <PillButton
            style="solid"
            size="medium"
            color={Color.LightGray}
            overrideTextColor={textColor}
            text={loadMoreText}
            onClick={() =>
              setVisibleCount(
                (currentCount) => currentCount + initialArticleCount
              )
            }
            icon={<PlusIcon className={getFillColorClass(textColor)} />}
          />
        </div>
      )}
    </>
  )
}

const ArticleFilterSection = ({
  callout,
  superscriptText,
  headingText,
  highlightedText,
  headingBodyText,
  searchPlaceholder = "Search",
  allTabLabel = "All",
  sortLabel = "Sort",
  latestOptionLabel = "Latest",
  applyFilterText = "Apply",
  loadMoreText = "Learn more",
  initialNumArticles,
  emptyStateText = "No articles here yet.",
  textColor = Color.Charcoal,
  ...props
}: ArticleFilterSectionProps) => {
  const backgroundColor = getAccentColor(textColor)
  const activeCallout = callout?.text?.trim() ? callout : undefined

  return (
    <GridSection primaryBackgroundColor={backgroundColor} {...props}>
      {/* The navigation is absolutely positioned until the page scrolls, so it
      reserves no space and the first section has to clear it itself. */}
      <div className="col-span-full mb-6 mt-32 flex flex-col items-center gap-8 text-center md:mb-0 md:mt-0">
        {activeCallout && (
          <Callout
            iconClassName="!-mt-[3px]"
            text={activeCallout.text}
            textSize="subscript-lg"
            highlightedWord={activeCallout.highlightedWord}
            highlightColor={activeCallout.highlightColor}
            highlightSize="subscript-lg"
            hasBorder={activeCallout.hasBorder ?? false}
            hasIcon={activeCallout.hasIcon}
            iconUrl={activeCallout.icon?.filename}
            iconAlt={activeCallout.icon?.alt || ""}
            iconColor={activeCallout.iconColor}
            ctaText={activeCallout.ctaText}
            ctaLink={storyblokLinkToYocoLink(activeCallout.ctaLink)}
            color={textColor}
          />
        )}

        {!activeCallout && superscriptText && (
          <Typography
            size="subscript-lg"
            font="grotesk"
            weight="book"
            color={textColor}
            text={superscriptText}
          />
        )}

        {headingText && (
          <Typography
            element="h1"
            size="text-hero-h3"
            font="grotesk-25"
            weight="medium"
            color={textColor}
            text={headingText}
            highlightedText={highlightedText}
            highlightSize="text-hero-h3"
            highlightWeight="medium"
            highlightClassName="block bg-heading-blue-charcoal bg-no-repeat bg-clip-text !text-transparent"
          />
        )}

        {headingBodyText && (
          <Typography
            size="body-md"
            font="grotesk"
            weight="book"
            color={textColor}
            text={headingBodyText}
          />
        )}
      </div>

      <Formik initialValues={INITIAL_SEARCH_VALUES} onSubmit={() => {}}>
        <ArticleList
          searchPlaceholder={searchPlaceholder}
          allTabLabel={allTabLabel}
          sortLabel={sortLabel}
          latestOptionLabel={latestOptionLabel}
          applyFilterText={applyFilterText}
          loadMoreText={loadMoreText}
          emptyStateText={emptyStateText}
          textColor={textColor}
          initialArticleCount={parseInitialArticleCount(initialNumArticles)}
        />
      </Formik>
    </GridSection>
  )
}

export default ArticleFilterSection
